import { toWebp } from '@/utils/common';
import Image from 'next/image';
import React, { useState, useEffect, useRef } from 'react';

interface SliderComponentProps {
  data: any;
}

const SliderComponent = ({ data }: SliderComponentProps) => {
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const slides =
    data?.cards?.map((card: any) => ({
      id: card?.id,
      title: card?.title,
      Image:
        toWebp(
          card?.image?.data?.attributes?.url &&
          baseUrl + card?.image?.data?.attributes?.url),
      MobileImage:
        toWebp(
          card?.mobile_image?.data?.attributes?.url &&
          baseUrl + card?.mobile_image?.data?.attributes?.url),
    })) || [];

  const tabs = slides.map((s: any) => s.title);

  // refs for each tab
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Auto-play
  useEffect(() => {
    if (!slides.length) return;

    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 4000);
    } else {
      clearInterval(intervalRef.current!);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPaused, slides.length]);

  // Scroll active tab into center on mobile
  useEffect(() => {
    const activeTab = tabRefs.current[currentSlide];
    const parent = activeTab?.parentElement;
  
    if (activeTab && parent) {
      const parentWidth = parent.clientWidth;
      const tabOffsetLeft = activeTab.offsetLeft;
      const tabWidth = activeTab.clientWidth;
  
      parent.scrollTo({
        left: tabOffsetLeft - parentWidth / 2 + tabWidth / 2,
        behavior: "smooth",
      });
    }
  }, [currentSlide]);

  const handleTabClick = (index: number) => setCurrentSlide(index);

  if (!slides.length) {
    return (
      <div className="text-center py-10 text-gray-400">
        No slides available
      </div>
    );
  }

  return (
    <div className="w-full mx-auto text-white rounded-lg overflow-hidden">
      {/* Tab Navigation */}
      <div className="flex sm:mb-[32px] mb-[40px] pt-[50px] xl:pt-[60px] relative overflow-x-auto whitespace-nowrap scrollbar-hide">
        {tabs.map((tab: any, index: number) => (
          <button
            key={index}
            ref={(el) => { tabRefs.current[index] = el; }}
            onClick={() => handleTabClick(index)}
            className={`flex-1 text-heading-6 border-t min-w-[151px] xl:mx-[17.5px] mx-[12.5px] border-b border-[rgba(50,52,59,1)] xl:px-[60px] xl:py-[22px] px-[25px] sm:py-[21px_17px] py-[16px_14px] text-sm font-medium sm:h-auto h-[56px] relative ${
              currentSlide === index
                ? 'text-white bg-[#27282f] border-t border-b border-solid [border-width:1px_0_1px_0] [border-image-source:linear-gradient(270deg,rgba(138,188,0,0)_1.84%,#8ABC00_36.2%,#0086E6_68.02%,rgba(0,134,230,0)_100%)] [border-image-slice:1]'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {tab}
            {currentSlide === index && (
              <div className="absolute xl:top-[-65px] sm:top-[-45px] top-[-35px] z-[-1] left-0 text-display text-stroke-1 right-0 text-transparent">
                {"0" + (index + 1)}
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Slider */}
      <div
        className="relative overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div
          className="flex transition-transform duration-500 ease-in-out h-full"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides?.map((slide: any, index: number) => (
            <div
              key={index}
              className="w-full flex-shrink-0 relative sm:px-0 px-4"
            >
              <div className="h-full relative overflow-hidden">
                <div className="z-10">
                  <Image
                    alt={slide?.title}
                    src={slide?.Image}
                    width={1112}
                    height={437}
                    className="mx-auto w-full sm:block hidden"
                  />
                  <Image
                    alt={slide?.title}
                    src={slide?.MobileImage}
                    width={323}
                    height={378}
                    className="mx-auto w-full h-[378px] sm:hidden block"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Progress Indicators (Mobile) */}
      <div className="flex justify-center py-4 space-x-2 sm:hidden">
        {slides.map((_: any, index: number) => (
          <button
            key={index}
            onClick={() => handleTabClick(index)}
            className={`w-[16px] h-[4px] rounded-full transition-all duration-300 ${
              currentSlide === index
                ? 'bg-white w-[140px] bg-gradient-to-r from-[#8ABC00] to-[#0086E6]'
                : 'bg-gray-700 hover:bg-gray-700'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default SliderComponent;
